import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeIntroPopup,
  setIntroTransitionActive,
  setShowGameAfterTransition,
} from "../../../State/actions";
import {
  selectIntroTransitionActive,
  selectHoldAndWinActive,
} from "../../../State/selectors";
import { useAppActivity } from "../../../Contexts/AppActivityContext";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import anime from "animejs";
import * as PIXI from "pixi.js";

const IntroTransitionOverlay = () => {
  const dispatch = useDispatch();
  const { startIdleTimer } = useAppActivity();
  const { dimensions } = useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();
  const overlayRef = useRef(null);
  const animationRef = useRef(null);
  const introTransitionActive = useSelector(selectIntroTransitionActive);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const { backgroundMusicEnabled } = useSelector((state) => state.sound);

  // Memoize container zIndex
  const containerZIndex = useMemo(() => 10000, []); // Above absolutely everything

  // Create gradient overlay matching the loader background
  const createGradientOverlay = useCallback(() => {
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const ctx = canvas.getContext("2d");

    // Diagonal linear gradient (top-left → bottom-right) — matches loader
    const linear = ctx.createLinearGradient(0, 0, dimensions.width, dimensions.height);
    linear.addColorStop(0, "#2a1f5e");
    linear.addColorStop(0.4, "#150e3a");
    linear.addColorStop(1, "#050020");
    ctx.fillStyle = linear;
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    const texture = PIXI.Texture.from(canvas);
    const sprite = new PIXI.Sprite(texture);
    sprite.width = dimensions.width;
    sprite.height = dimensions.height;
    return sprite;
  }, [dimensions.width, dimensions.height]);

  // Handle transition animation
  useEffect(() => {
    if (introTransitionActive && overlayRef.current) {
      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Remove old overlay and create new one
      overlayRef.current.removeChildren();
      overlayRef.current.addChild(createGradientOverlay());

      // Create cinematic transition: fade in black → pause → fade out to reveal game
      const animation = anime.timeline({
        complete: () => {
          dispatch(setIntroTransitionActive(false));
          startIdleTimer();
        },
      });

      // Phase 1: Fade in black overlay to cover the intro screen
      animation.add({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 200,
        easing: "easeInQuad",
        complete: () => {
          // Screen is now entirely black - clear background animation and remove intro
          const bgController = backgroundAnimationControllerRef.current;
          if (bgController?.backgroundSpine) {
            // Clear all animation tracks
            if (bgController.backgroundSpine.state) {
              bgController.backgroundSpine.state.clearTracks();
            }
            // Remove front_mask slot attachments (flowers/tree) so they disappear
            bgController.stopFrontMask();
            // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
            bgController.backgroundSpine.skeleton.setBonesToSetupPose();
            // Play appropriate idle animation
            if (holdAndWinActive) {
              bgController.playFeatureIdle?.();
            } else {
              bgController.playBaseIdle?.();
            }
          }

          // Remove intro and show game underneath
          dispatch(closeIntroPopup());
          dispatch(setShowGameAfterTransition(true));
        },
      });

      // Phase 2: Brief dramatic pause with full black coverage
      animation.add({
        targets: overlayRef.current,
        alpha: 1,
        duration: 200,
      });

      // Phase 3: Fade out black overlay to reveal the game
      animation.add({
        targets: overlayRef.current,
        alpha: [1, 0],
        duration: 200,
        easing: "easeOutQuad",
        complete: () => {
          // Game is now fully visible
          // Start background music if enabled and not in Hold and Win feature
          if (backgroundMusicEnabled && !holdAndWinActive) {
            soundManager.play(SOUND_NAME.background_music);
          }
        },
      });

      animationRef.current = animation;

      return () => {
        if (animationRef.current) {
          animationRef.current.pause();
          animationRef.current = null;
        }
      };
    }
  }, [
    introTransitionActive,
    createGradientOverlay,
    dispatch,
    startIdleTimer,
    backgroundAnimationControllerRef,
    holdAndWinActive,
    backgroundMusicEnabled,
  ]);

  // Only render when there's an active transition
  if (!introTransitionActive) return null;

  return (
    <Container
      name="intro_transition_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={containerZIndex}
    />
  );
};

export default IntroTransitionOverlay;
